import React, { useEffect, useState } from 'react';
import { Zap, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';

export const isFlashActive = (product) =>
  !!(
    product?.flash_sale_price &&
    product?.flash_sale_end &&
    product.flash_sale_price < product.price &&
    (!product.flash_sale_start || new Date(product.flash_sale_start) <= new Date()) &&
    new Date(product.flash_sale_end) > new Date()
  );

export const isFlashScheduled = (product) =>
  !!(
    product?.flash_sale_price &&
    product?.flash_sale_end &&
    product?.flash_sale_start &&
    product.flash_sale_price < product.price &&
    new Date(product.flash_sale_start) > new Date() &&
    new Date(product.flash_sale_end) > new Date()
  );

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

export const FlashSaleCountdown = ({ endDate, size = 'sm', onExpire, testId = 'flash-countdown', upcoming = false }) => {
  const { language } = useLanguage();
  const T = usePageT();
  const [remaining, setRemaining] = useState(() => new Date(endDate).getTime() - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const r = new Date(endDate).getTime() - Date.now();
      setRemaining(r);
      if (r <= 0) {
        clearInterval(timer);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  if (remaining <= 0) return null;

  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const dayUnit = language === 'fr' ? 'j' : 'd';
  const time = `${days > 0 ? `${days}${dayUnit} ` : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  const box = upcoming ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const accent = upcoming ? 'text-amber-600' : 'text-red-600';
  const strong = upcoming ? 'text-amber-700' : 'text-red-700';
  const label = upcoming
    ? T('Bientôt : vente flash — début dans', 'Coming soon: flash sale — starts in')
    : T('Vente Flash — se termine dans', 'Flash Sale — ends in');

  if (size === 'lg') {
    return (
      <div className={`inline-flex items-center gap-3 border rounded-xl px-4 py-3 ${box}`} data-testid={testId}>
        <Zap className={`w-5 h-5 ${accent} ${upcoming ? 'fill-amber-500' : 'fill-red-600'}`} />
        <div>
          <p className={`text-xs font-bold uppercase tracking-widest ${accent}`}>
            {label}
          </p>
          <p className={`text-xl font-bold tabular-nums ${strong}`}>{time}</p>
        </div>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 tabular-nums ${accent} ${upcoming ? 'bg-amber-50' : 'bg-red-50'}`}
      data-testid={testId}
    >
      <Clock className="w-3 h-3" />
      {time}
    </span>
  );
};
